/**
 * Terminal utility functions.
 *
 * @module components/terminal/utils
 */

import type { Terminal } from '@xterm/xterm';

/**
 * Options for creating a terminal keyboard handler.
 */
export interface TerminalKeyHandlerOptions {
  /** The xterm.js Terminal instance */
  terminal: Terminal;
  /**
   * Send raw bytes to the PTY, bypassing xterm's input pipeline. Required for
   * synthetic readline sequences like Ctrl+Backspace → ESC+DEL — these need to
   * reach the PTY raw so the shell interprets them, not as bracketed-paste text.
   */
  sendInput?: (data: string) => void;
  /** Callback to check if the terminal is connected. Required for interactive terminals. */
  isConnected?: () => boolean;
  /** Whether this is a read-only terminal (disables paste and Ctrl shortcuts) */
  readOnly?: boolean;
}

/** Copy the current xterm selection to the clipboard. */
function copySelectionToClipboard(terminal: Terminal): boolean {
  const selection = terminal.getSelection();
  if (!selection) {
    return false;
  }
  navigator.clipboard.writeText(selection).catch((err) => {
    console.warn('[Terminal] Clipboard write failed:', err);
  });
  return true;
}

/**
 * Read the clipboard and paste into the terminal using xterm's bracketed-paste-aware
 * paste API. Shows a warning line in the terminal if the clipboard read is rejected
 * so the user isn't left wondering why nothing happened.
 */
function pasteFromClipboard(terminal: Terminal, isConnected?: () => boolean): void {
  if (isConnected && !isConnected()) {
    return;
  }
  navigator.clipboard
    .readText()
    .then((text) => {
      if (!text) {
        return;
      }
      if (isConnected && !isConnected()) {
        return;
      }
      // terminal.paste() wraps the payload in \e[200~..\e[201~ when the shell
      // has enabled bracketed-paste mode, preventing embedded newlines from
      // auto-executing the first line of a multi-line paste.
      terminal.paste(text);
    })
    .catch((err) => {
      console.warn('[Terminal] Clipboard read failed:', err);
      terminal.writeln('\r\n\x1b[33m[Paste failed — clipboard read denied]\x1b[0m');
    });
}

/**
 * Creates a keyboard event handler for xterm.js terminals.
 *
 * Captures copy/paste shortcuts so the browser doesn't steal them and so multi-line
 * pastes use bracketed-paste mode (instead of auto-executing the first line):
 *
 * | Shortcut                       | Behaviour                                                                |
 * |--------------------------------|--------------------------------------------------------------------------|
 * | Ctrl+Shift+C / Cmd+C           | Copy selection                                                           |
 * | Ctrl+Shift+V / Cmd+V           | Paste via bracketed-paste                                                |
 * | Ctrl+C (no Shift)              | Copy if selection exists, else fall through to xterm → SIGINT            |
 * | Ctrl+V (no Shift)              | Paste via bracketed-paste                                                |
 * | Ctrl+Backspace                 | Delete previous word (sends ESC+DEL → readline backward-kill-word)       |
 *
 * Plain Ctrl+V must be intercepted explicitly. xterm's default keydown handler
 * treats Ctrl+V as quoted-insert (`\x16`) and calls preventDefault, so the
 * browser never fires the native `paste` event. Without this intercept,
 * pressing Ctrl+V looks like nothing happened.
 *
 * Also forwards a curated list of Ctrl+key combos to the terminal so the browser
 * doesn't act on them (Ctrl+W close tab, Ctrl+L focus address bar, etc.).
 *
 * @param options - Configuration options
 * @returns A function suitable for terminal.attachCustomKeyEventHandler()
 */
export function createTerminalKeyHandler(options: TerminalKeyHandlerOptions) {
  const { terminal, sendInput, isConnected, readOnly = false } = options;

  return (event: KeyboardEvent): boolean => {
    if (event.type !== 'keydown') {
      return true;
    }

    const key = event.key.toLowerCase();

    // Ctrl+Shift+C — explicit copy (avoids triggering DevTools)
    if (event.ctrlKey && event.shiftKey && key === 'c') {
      copySelectionToClipboard(terminal);
      event.preventDefault();
      return false;
    }

    // Ctrl+Shift+V — explicit paste
    if (event.ctrlKey && event.shiftKey && key === 'v') {
      if (!readOnly) {
        pasteFromClipboard(terminal, isConnected);
      }
      event.preventDefault();
      return false;
    }

    // Cmd+C / Cmd+V on macOS (no Ctrl)
    if (event.metaKey && !event.ctrlKey) {
      if (key === 'c') {
        copySelectionToClipboard(terminal);
        event.preventDefault();
        return false;
      }
      if (key === 'v') {
        if (!readOnly) {
          pasteFromClipboard(terminal, isConnected);
        }
        event.preventDefault();
        return false;
      }
    }

    // For read-only terminals, don't capture any other shortcuts
    if (readOnly) {
      return true;
    }

    // Plain Ctrl+V (no Shift, no Alt, no Meta) — paste via bracketed-paste.
    // We MUST intercept this: xterm's default keydown handler turns Ctrl+V
    // into quoted-insert (\x16) and calls preventDefault, so the browser
    // never fires the native paste event. Without this branch Ctrl+V
    // silently does nothing visible to the user.
    if (event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey && key === 'v') {
      pasteFromClipboard(terminal, isConnected);
      event.preventDefault();
      return false;
    }

    // Plain Ctrl+C (no Shift, no Alt, no Meta) — VS Code style: if there is a
    // selection, copy it and clear the selection. Otherwise fall through so
    // xterm sends ^C (SIGINT) to the PTY.
    if (event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey && key === 'c') {
      if (terminal.hasSelection()) {
        copySelectionToClipboard(terminal);
        terminal.clearSelection();
        event.preventDefault();
        return false;
      }
      // No selection — let xterm emit ^C → SIGINT
      return true;
    }

    // Ctrl+Backspace — browsers send \x08 which readline maps to
    // backward-delete-char (single char). Send ESC+DEL straight to the PTY so
    // readline interprets it as backward-kill-word (same bytes Alt+Backspace
    // sends today). Going via sendInput rather than terminal.paste() ensures
    // the sequence is NOT wrapped in bracketed-paste markers.
    if (
      event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey &&
      !event.metaKey &&
      event.key === 'Backspace'
    ) {
      if ((isConnected?.() ?? true) && sendInput) {
        sendInput('\x1b\x7f');
      }
      event.preventDefault();
      return false;
    }

    // Only intercept other shortcuts when terminal is focused and connected
    if (!isConnected?.()) {
      return true;
    }

    // List of Ctrl+ shortcuts to capture for the terminal instead of browser
    const ctrlShortcuts = [
      'w', // Close tab
      't', // New tab
      'n', // New window
      'k', // Clear terminal
      'l', // Clear screen
      'u', // Clear line
      'a', // Start of line
      'e', // End of line
      'd', // EOF / exit
      'z', // Suspend
    ];

    if (event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey) {
      if (ctrlShortcuts.includes(key)) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
    }

    return true;
  };
}

/**
 * Extracts all text content from an xterm.js terminal buffer.
 *
 * @param terminal - The xterm.js Terminal instance
 * @returns The terminal buffer content as a string, or empty string if terminal is null
 */
export function getTerminalContent(terminal: Terminal | null): string {
  if (!terminal) return '';

  const buffer = terminal.buffer.active;
  let text = '';
  for (let i = 0; i < buffer.length; i++) {
    const line = buffer.getLine(i)?.translateToString(true);
    if (line) {
      text += line + '\n';
    }
  }
  return text.trim();
}

/**
 * Copies terminal content to clipboard.
 *
 * @param terminal - The xterm.js Terminal instance
 * @returns Promise that resolves to true if copy succeeded, false otherwise
 */
export async function copyTerminalContent(terminal: Terminal | null): Promise<boolean> {
  const content = getTerminalContent(terminal);
  if (!content) return false;

  try {
    await navigator.clipboard.writeText(content);
    return true;
  } catch (err) {
    console.error('[Terminal] Failed to copy:', err);
    return false;
  }
}
